/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { memo } from "react";
import { useFormContext } from "react-hook-form";

import { getProhibitedCharacters } from "utilities/getProhibitedCharacters.js";
import { isEmpty } from "utilities/string";

import ValidationErrorText from "components/ui/validation/ValidationErrorText";

// CSS読み込み
import {
  inputWrap,
  inputArea_text,
  inputArea_text_error,
} from "assets/css/stylesCss";

// JS読み込み
import {
  formatTelNumber,
} from "utilities/formater";

import {
  replaceHyphens,
} from "utilities/string";

/**
 * 入力フォーム
 * @param {String} name react-hook-form管理用フォーム特定文字列
 * @param {*} value 
 * @param {String} placeholder プレースホルダー表示用文字列
 * @param {String} maxLength 最大文字数
 * @param {String} inputMode 入力されるデータの型
 * @param {Object} rules バリデーション設定オブジェクト
 * @param {Object} mailAddressValid バリデーションメールアドレス形式判定用オブジェクト
 * @param {Object} matchValid バリデーション一致判定用オブジェクト
 * @param {Boolean} isTelNo true :電話番号のテキストボックス
 *                          false:電話番号以外のテキストボックス
 * @param {Object} telNoValid バリデーション電話番号形式判定用オブジェクト
 * @param {Boolean} required true :必須項目
 *                           false:任意項目
 */
const InputText = memo(({ name, value, placeholder, maxLength, inputMode, rules, mailAddressValid, matchValid, isTelNo, telNoValid, pairNumber, required }) => {

  const {
    register,
    formState: { errors },
    getValues,
    setValue,
    clearErrors,
  } = useFormContext();

  return (
    <>
      <div css={inputWrap}>
        <input
          {...register(name, {
            ...rules,
            validate: {
              // 禁止文字チェック
              isOnlyAllowedCharacters: (value) => {
                if (getProhibitedCharacters(value).length === 0) {
                  return true;
                } else {
                  return "利用できない文字が含まれています。";
                }
              },

              // メールアドレスフォーマットチェック
              isValidMailAddress: () => {
                if (
                  !mailAddressValid ||
                  Object.keys(mailAddressValid).length === 0
                ) {
                  return true;
                }
                if (mailAddressValid.mailAddressValidationCheck(getValues(name))) {
                  return true;
                } else {
                  return mailAddressValid.message;
                }
              },

              // 一致チェック
              isValidMatch: () => {
                // matchValidが指定されていない場合
                if (!matchValid || Object.keys(matchValid).length === 0) {
                  return true;
                }

                // 比較対象が存在しない場合
                if (!getValues(matchValid.targetName)) {
                  return true;
                }

                // 一致している場合
                if (getValues(matchValid.targetName) === getValues(name)) {
                  // どちらのエラーも解除し、メッセージを非表示にする
                  clearErrors(matchValid.targetName, name);
                  return true;
                } else {
                  return matchValid.message;
                }
              },

              // 電話番号フォーマットチェック
              isValidFormatTelNo: () => {
                // telNoValidが指定されていない場合
                if (!telNoValid || Object.keys(telNoValid).length === 0) {
                  return true;
                }

                // 携帯電話または固定電話が未入力の場合
                if (isEmpty(getValues("phoneNo")) && isEmpty(getValues("telNo"))) {
                  return "携帯電話番号または固定電話番号のいずれかが必須です。";
                }

                // 電話番号が入力されている場合
                if(!isEmpty(getValues(name))) {
                  // 電話番号の形式が正しい場合
                  if (formatTelNumber(getValues(name))) {
                    return true;
                  } else {
                    return telNoValid.message;
                  }
                } else {
                  return true;
                }
              },
            },
            onBlur: () => {
              // ボタン活性
              setValue("isActive", true, { shouldValidate: true });

              // 前後のスペースを削除し設定
              setValue(name, getValues(name).trim());

              // 値が存在する場合
              if (getValues(name)) {
                // 電話番号以外の項目の場合、強制的に入力チェックを実施
                if (!telNoValid || Object.keys(telNoValid).length === 0) {
                  setValue(name, getValues(name), { shouldValidate: true });
                } else {
                  // 正しい電話番号が入力されている場合は、ハイフンを付与
                  if (formatTelNumber(getValues(name))) {
                    setValue(name, formatTelNumber(getValues(name)), { shouldValidate: true });
                    // 電話番号いずれか必須チェックのエラーメッセージ解除
                    if( isEmpty(getValues(pairNumber))) clearErrors(pairNumber);
                  } else {
                    // 強制的に入力チェックを実施
                    setValue(name, getValues(name), {
                      shouldValidate: true,
                    });
                  }
                }
              }
            },
            onChange: () => {
              // 初期化
              clearErrors(name);

              // 値が存在しない場合
              if (!getValues(name)) {
                // ボタン活性
                setValue("isActive", true);
              } else {
                // ボタン非活性
                setValue("isActive", false);
              }
            },
          })}
          css={[
            errors[name] ? inputArea_text_error : inputArea_text
          ]}
          value={value}
          placeholder={placeholder}
          maxLength={maxLength ? maxLength : ""}
          inputMode={inputMode ? inputMode : ""}
          onFocus={() => {
            if (isTelNo) {
              setValue(name, replaceHyphens(getValues(name)));
            }
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') e.preventDefault();
          }}
        />
      </div>

      {/* エラーメッセージ **/}
      {errors[name] && (
        <ValidationErrorText>{errors[name].message}</ValidationErrorText>
      )}
    </>
  );
});

export default InputText;